/**
 * Natural date/time INPUT conversion — the shared logic behind the editable
 * date/time fields (ShiftOverview's ShiftTimeInput and the TimeEditor). Pure and
 * testable: text → parts, parts → a base date, and back to editable display text.
 *
 * Natural *date* parsing ("feb 5", "12/25/2024") lives in `@/lib/dateParser`
 * (`parseFlexibleDate`); this module covers time, duration, and the merge/format.
 */

/** Minimum duration step (minutes) used when clamping parsed durations. */
export const DEFAULT_STEP_MINUTES = 30;

/**
 * Format a date's time as 12-hour display text.
 * @example
 * formatTime12(new Date("2026-01-01T20:05:00")) // => "8:05 PM"
 */
export function formatTime12(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/**
 * Format a date as editable `M/D/YYYY` text.
 * @example
 * formatDateEditable(new Date(2026, 6, 23)) // => "7/23/2026"
 */
export function formatDateEditable(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/**
 * Format a minute count as `Hh MMm` duration text.
 * @example
 * formatDuration(485) // => "8h 05m"
 */
export function formatDuration(totalMin: number): string {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Parse natural duration text into minutes (clamped to at least `stepMinutes`),
 * or null if unparseable. Accepts `8h 30m`, `8h`, `45m`, and bare decimals as
 * hours (`1.5` → 90).
 * @example
 * parseDurationText("8h 30m") // => 510
 * parseDurationText("1.5")    // => 90
 * parseDurationText("")       // => null
 */
export function parseDurationText(text: string, stepMinutes = DEFAULT_STEP_MINUTES): number | null {
  const cleaned = text.trim().toLowerCase();
  if (!cleaned || cleaned === "0") return null;

  const hm = cleaned.match(/^(\d+)h\s*(\d+)m?$/);
  if (hm) return Math.max(stepMinutes, parseInt(hm[1]) * 60 + parseInt(hm[2]));

  const hOnly = cleaned.match(/^(\d+)h$/);
  if (hOnly) return Math.max(stepMinutes, parseInt(hOnly[1]) * 60);

  const mOnly = cleaned.match(/^(\d+)m$/);
  if (mOnly) return Math.max(stepMinutes, parseInt(mOnly[1]));

  const decimal = cleaned.match(/^(\d+\.?\d*)$/);
  if (decimal) return Math.max(stepMinutes, Math.round(parseFloat(decimal[1]) * 60));

  return null;
}

/**
 * Flexible time parser. Normalises typo separators (`;` `.` → `:`), accepts
 * military time (`1300`, `0800`), packed digits (`530` → 5:30), bare hours (`8`),
 * and any meridiem spelling (`am`, `a.m.`, `PM`, …). Without a meridiem, hours
 * 1–12 are AM and 13–23 stay military.
 * @example
 * parseTimeText("8:30pm") // => { hours: 20, minutes: 30 }
 * parseTimeText("1300")   // => { hours: 13, minutes: 0 }
 * parseTimeText("530")    // => { hours: 5, minutes: 30 }
 * parseTimeText("nope")   // => null
 */
export function parseTimeText(text: string): { hours: number; minutes: number } | null {
  let cleaned = text.trim().toLowerCase();
  if (!cleaned) return null;

  cleaned = cleaned.replace(/[;]/g, ":");

  const meridiemMatch = cleaned.match(/\s*([ap]\.?m\.?)\s*$/);
  const meridiem = meridiemMatch ? (meridiemMatch[1].startsWith("p") ? "pm" : "am") : null;
  if (meridiemMatch) cleaned = cleaned.slice(0, meridiemMatch.index).trim();

  let h: number | null = null;
  let m = 0;

  const colonMatch = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (colonMatch) {
    h = parseInt(colonMatch[1]);
    m = parseInt(colonMatch[2]);
  }

  if (h === null) {
    const four = cleaned.match(/^(\d{2})(\d{2})$/);
    if (four) {
      h = parseInt(four[1]);
      m = parseInt(four[2]);
    }
  }

  if (h === null) {
    const three = cleaned.match(/^(\d)(\d{2})$/);
    if (three) {
      h = parseInt(three[1]);
      m = parseInt(three[2]);
    }
  }

  if (h === null) {
    const bare = cleaned.match(/^(\d{1,2})$/);
    if (bare) {
      h = parseInt(bare[1]);
      m = 0;
    }
  }

  if (h === null || m < 0 || m >= 60) return null;

  if (meridiem === "pm" && h < 12) h += 12;
  if (meridiem === "am" && h === 12) h = 0;

  if (h < 0 || h >= 24) return null;

  return { hours: h, minutes: m };
}

/**
 * Apply parsed `{hours, minutes}` onto a base date (keeps the date, zeroes
 * seconds/ms), returning a new Date.
 * @example
 * applyTimeParts(new Date(2026, 0, 1, 9), { hours: 20, minutes: 30 }) // => 2026-01-01 20:30
 */
export function applyTimeParts(base: Date, { hours, minutes }: { hours: number; minutes: number }): Date {
  const d = new Date(base);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

/**
 * Apply a parsed date's Y/M/D onto a base date (keeps the base time), returning
 * a new Date.
 * @example
 * applyDateParts(new Date(2026, 0, 1, 9, 30), new Date(2026, 1, 5)) // => 2026-02-05 09:30
 */
export function applyDateParts(base: Date, parsed: Date): Date {
  const d = new Date(base);
  d.setFullYear(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  return d;
}
